<template>
  <component
    :is="rootTag"
    v-bind="rootAttrs"
    class="stat-card"
    :class="[
      `stat-card--${effectiveAccent}`,
      { 'stat-card--negative': isNegative, 'stat-card--clickable': !!to, 'stat-card--soft': soft },
    ]"
    :title="tooltip"
    @click="onActivate"
    @keydown.enter.prevent="onActivate"
  >
    <div class="stat-card__accent-bar" />
    <div class="stat-card__content">
      <div class="stat-card__icon-wrap">
        <component :is="icon" class="stat-card__icon" />
      </div>
      <div class="stat-card__body">
        <span v-if="statusBadge" class="stat-card__badge">{{ statusBadge }}</span>
        <p class="stat-card__value">
          <template v-if="animatedNumber !== null">{{ formatValue(animatedNumber) }}</template>
          <template v-else>—</template>
        </p>
        <p class="stat-card__label">{{ label }}</p>
      </div>
    </div>
  </component>
</template>

<script setup>
import { ref, watch, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps({
  label: { type: String, required: true },
  /** `null` = donnée pas encore disponible : la carte affiche « — », pas un zéro trompeur. */
  value: { type: Number, default: null },
  icon: { type: Object, required: true },
  /** Optionnel, ex: " F CFA" pour les montants */
  suffix: { type: String, default: '' },
  accent: {
    type: String,
    default: 'primary',
    validator: (v) => ['primary', 'green', 'blue', 'orange', 'purple', 'red'].includes(v),
  },
  /** Affiche la carte en variant négatif (valeur en rouge, barre rouge) */
  status: { type: String, default: '', validator: (v) => ['', 'negative', 'positive', 'warning'].includes(v) },
  /** Badge de contexte (ex. "À surveiller", "Inférieur au seuil recommandé") */
  statusBadge: { type: String, default: '' },
  /** Tooltip au survol */
  tooltip: { type: String, default: '' },
  /** Route vue-router (carte cliquable) */
  to: { type: [String, Object], default: '' },
  /** Variante crème compacte de l'espace client (icône au-dessus, sans barre d'accent). */
  soft: { type: Boolean, default: false },
});

const router = useRouter();

const hasValue = computed(() => props.value !== null && props.value !== undefined);
const isNegative = computed(
  () => props.status === 'negative' || (hasValue.value && props.value < 0 && props.status !== 'positive')
);
const effectiveAccent = computed(() => (isNegative.value ? 'red' : props.accent));

const rootTag = computed(() => (props.to ? 'router-link' : 'div'));
const rootAttrs = computed(() => {
  if (!props.to) return {};
  return { to: props.to };
});

function onActivate() {
  if (!props.to) return;
  router.push(props.to);
}

const animatedNumber = ref(props.value);

function formatValue(n) {
  if (props.suffix) {
    const formatted = Number(n).toLocaleString('fr-FR', { maximumFractionDigits: 0 });
    return `${formatted}${props.suffix}`;
  }
  return n;
}

function animateValue(from, to, duration = 600) {
  const start = performance.now();
  const step = (now) => {
    const elapsed = now - start;
    const progress = Math.min(elapsed / duration, 1);
    const easeOut = 1 - (1 - progress) ** 2;
    animatedNumber.value = Math.round(from + (to - from) * easeOut);
    if (progress < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

watch(
  () => props.value,
  (newVal, oldVal) => {
    if (newVal === null || newVal === undefined) {
      animatedNumber.value = null;
      return;
    }
    animateValue(oldVal ?? 0, newVal, 500);
  }
);

onMounted(() => {
  if (hasValue.value && props.value > 0) {
    animateValue(0, props.value, 600);
  } else {
    animatedNumber.value = hasValue.value ? props.value : null;
  }
});
</script>

<style scoped>
.stat-card {
  @apply relative overflow-hidden rounded-2xl bg-white p-5 border border-slate-100/80;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.stat-card--clickable {
  text-decoration: none;
  color: inherit;
  cursor: pointer;
}

@media (hover: hover) {
  .stat-card:hover,
  .stat-card--clickable:hover {
    transform: translateY(-6px) scale(1.01);
    box-shadow: 0 24px 48px -12px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.03);
  }
}

.stat-card__accent-bar {
  @apply absolute left-0 top-0 bottom-0 w-1 rounded-l-full;
}

.stat-card--primary .stat-card__accent-bar {
  background: linear-gradient(180deg, #d89c24 0%, #b8821a 100%);
}

.stat-card--green .stat-card__accent-bar {
  background: linear-gradient(180deg, #22c55e 0%, #16a34a 100%);
}

.stat-card--blue .stat-card__accent-bar {
  background: linear-gradient(180deg, #3b82f6 0%, #2563eb 100%);
}

.stat-card--orange .stat-card__accent-bar {
  background: linear-gradient(180deg, #f97316 0%, #ea580c 100%);
}

.stat-card--purple .stat-card__accent-bar {
  background: linear-gradient(180deg, #8b5cf6 0%, #7c3aed 100%);
}

.stat-card--red .stat-card__accent-bar {
  background: linear-gradient(180deg, #ef4444 0%, #dc2626 100%);
}

.stat-card__content {
  @apply flex items-center gap-4 pl-1;
}

.stat-card__icon-wrap {
  @apply flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center;
}

.stat-card--primary .stat-card__icon-wrap {
  @apply bg-primary/10;
}

.stat-card--green .stat-card__icon-wrap {
  @apply bg-dashboard-accent-green/10;
}

.stat-card--blue .stat-card__icon-wrap {
  @apply bg-dashboard-accent-blue/10;
}

.stat-card--orange .stat-card__icon-wrap {
  @apply bg-dashboard-accent-orange/10;
}

.stat-card--purple .stat-card__icon-wrap {
  @apply bg-dashboard-accent-purple/10;
}

.stat-card--red .stat-card__icon-wrap {
  @apply bg-red-500/10;
}

.stat-card__icon {
  @apply w-6 h-6;
}

.stat-card--primary .stat-card__icon {
  @apply text-primary;
}

.stat-card--green .stat-card__icon {
  @apply text-dashboard-accent-green;
}

.stat-card--blue .stat-card__icon {
  @apply text-dashboard-accent-blue;
}

.stat-card--orange .stat-card__icon {
  @apply text-dashboard-accent-orange;
}

.stat-card--purple .stat-card__icon {
  @apply text-dashboard-accent-purple;
}

.stat-card--red .stat-card__icon {
  @apply text-red-600;
}

.stat-card__body {
  @apply min-w-0 flex-1;
}

.stat-card--negative .stat-card__value {
  @apply text-red-600;
}

.stat-card__value {
  @apply text-2xl font-bold text-slate-800 tabular-nums;
}

.stat-card__badge {
  @apply inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 mb-1;
}

.stat-card--negative .stat-card__badge {
  @apply bg-red-100 text-red-800;
}

.stat-card__label {
  @apply text-sm font-medium text-slate-500 mt-0.5;
}

/* ---- Variante « espace client » ---- */
.stat-card--soft {
  background: var(--client-surface-card, #f7f9fc);
  border: 1px solid var(--client-line, rgba(1, 38, 62, 0.1));
  border-radius: var(--client-radius, 20px);
  box-shadow: var(--client-shadow, none);
  padding: var(--space-5) var(--space-4);
}

@media (hover: hover) {
  .stat-card--soft:hover,
  .stat-card--soft.stat-card--clickable:hover {
    transform: none;
    box-shadow: var(--client-shadow, none);
    background: var(--client-surface-card-hover, #eef2f7);
  }
}

.stat-card--soft .stat-card__accent-bar {
  display: none;
}

.stat-card--soft .stat-card__content {
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--space-3);
  padding-left: 0;
}

.stat-card--soft .stat-card__icon-wrap {
  width: 40px;
  height: 40px;
  background: var(--brand-gold-soft);
}

.stat-card--soft .stat-card__icon {
  color: var(--brand-navy);
}

.stat-card--soft .stat-card__body {
  flex: 0 1 auto;
  width: 100%;
}

.stat-card--soft .stat-card__value {
  font-family: var(--font-body);
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--brand-navy);
  line-height: 1.2;
}

.stat-card--soft.stat-card--negative .stat-card__value {
  color: var(--brand-red);
}

.stat-card--soft .stat-card__label {
  font-family: var(--font-body);
  font-size: 0.8125rem;
  color: var(--client-text-muted, #64748b);
  margin-top: var(--space-1);
}

.stat-card--soft .stat-card__badge {
  background: var(--brand-gold-soft);
  color: var(--brand-navy);
  font-family: var(--font-body);
}

.stat-card--soft.stat-card--negative .stat-card__badge {
  background: var(--brand-red-soft);
  color: var(--brand-red);
}
</style>
